import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type {
  NotificationTypeFilterOption,
  OperationCodeOption,
  PagedResponse,
  SendStatusOption,
  SentEmailDetail,
  SentEmailListItem,
} from '../types'

const BASE = '/management/sent-emails'

/**
 * Liste/dışa aktarma filtreleri (078). Hepsi opsiyoneldir ve sunucuda **VE** mantığıyla birleşir.
 *
 * Tarihler ISO-8601 instant'tır; gün sınırlarını paylaşılan `DateRangeFilter` hesaplar (başlangıç
 * `00:00:00.000`, bitiş `23:59:59.999`) → sunucu saat dilimi dönüşümü yapmaz.
 */
export interface SentEmailFilters {
  /** Alıcı veya konu araması; `searchTerm` ile kapılanır (< 2 karakter gönderilmez). */
  q?: string
  /**
   * Bildirim kanalı (`REFERANS` TYPE=NOTIFICATION_TYPE; 60 Eposta · 62 PN · 63 Web). `null` →
   * filtresiz: ekran üç kanalı birlikte listeler.
   */
  notificationTypeId?: number | null
  sendStatusId?: number | null
  /**
   * Bildirimi doğuran olayın işlem kodu (`REF_OPERATION_CODE`). Kolonu **boş** olan satırlar hiçbir
   * seçimde dönmez — o satırların hangi olaydan doğduğu belirlenemiyor. Kolon eklenmeden önce
   * üretilmiş satırlar 2026-08-07'de geriye dönük dolduruldu (spec FR-016); e-posta kanalında boş
   * satır **kalmadı**.
   */
  operationCodeId?: number | null
  /** `RETRY_COUNT >= N`. **`0` geçerli bir değerdir** — bkz. {@link toParams}. */
  minRetryCount?: number | null
  plannedStart?: string | null
  plannedEnd?: string | null
  sendingStart?: string | null
  sendingEnd?: string | null
}

export interface SentEmailQuery extends SentEmailFilters {
  page: number
  size: number
}

/**
 * Filtreleri sorgu parametrelerine çevirir; boş olanlar `undefined` olur → axios onları sorguya
 * **eklemez**.
 *
 * <p><b>`minRetryCount` özel durumu:</b> yaygın `value || undefined` kalıbı `0`'ı da siler ve
 * kullanıcının bilinçli olarak girdiği "en az 0 deneme" filtresi sessizce düşerdi (kaynak §9.5
 * tuzağı). Bu yüzden `null`/`undefined` **açıkça** kontrol edilir.
 */
function toParams(filters: SentEmailFilters) {
  return {
    q: filters.q ? filters.q : undefined,
    notificationTypeId: filters.notificationTypeId ?? undefined,
    sendStatusId: filters.sendStatusId ?? undefined,
    operationCodeId: filters.operationCodeId ?? undefined,
    minRetryCount: filters.minRetryCount ?? undefined,
    plannedStart: filters.plannedStart ?? undefined,
    plannedEnd: filters.plannedEnd ?? undefined,
    sendingStart: filters.sendingStart ?? undefined,
    sendingEnd: filters.sendingEnd ?? undefined,
  }
}

/**
 * Bildirim gönderim listesi (üç kanal) — **sunucu-tarafı** sayfalama + filtreleme.
 *
 * `sort` **gönderilmez**: düzen sunucuda sabittir (`coalesce(SENDING_TIME, PLANNED_SENDING_TIME)
 * DESC, ID DESC`) ve kolon sıralaması bilinçli olarak kapalıdır (spec FR-004).
 */
export async function getSentEmails({
  page,
  size,
  ...filters
}: SentEmailQuery): Promise<PagedResponse<SentEmailListItem>> {
  const { data } = await apiClient.get<PagedResponse<SentEmailListItem>>(BASE, {
    params: { page, size, ...toParams(filters) },
  })
  return data
}

/** Tekil detay. Listede olmayan (silinmiş) bir kimlik **404** döner. */
export async function getSentEmail(id: number): Promise<SentEmailDetail> {
  const { data } = await apiClient.get<SentEmailDetail>(`${BASE}/${id}`)
  return data
}

/**
 * Bildirim tipi filtresinin seçenekleri — yalnız **aktif** `NOTIFICATION_TYPE` referansları, dile
 * göre çözülmüş adla. Sabit `60/62/63` listesi istemcide **tutulmaz**: katalog `REFERANS`'tadır ve
 * analist bir kanal eklerse (ör. 61 SMS) combo kendiliğinden genişler.
 */
export async function getSentEmailNotificationTypes(): Promise<NotificationTypeFilterOption[]> {
  const { data } = await apiClient.get<NotificationTypeFilterOption[]>(`${BASE}/notification-types`)
  return data
}

/** Durum filtresi seçenekleri — yalnız aktif `SEND_STATUS` referansları, dile göre çözülmüş adla. */
export async function getSendStatuses(): Promise<SendStatusOption[]> {
  const { data } = await apiClient.get<SendStatusOption[]>(`${BASE}/send-statuses`)
  return data
}

/**
 * İşlem Kodu filtresinin seçenekleri — yalnız **Bildirimler modülünün aktif** kodları, dile göre
 * çözülmüş adla. 059'un combosuyla aynı kaynaktan gelir ama **ayrı uçtur**: bu ekranın yetkisi
 * (`SENT_EMAIL_VIEW`) şablon yönetimi yetkisinden bağımsızdır.
 */
export async function getSentEmailOperationCodes(): Promise<OperationCodeOption[]> {
  const { data } = await apiClient.get<OperationCodeOption[]>(`${BASE}/operation-codes`)
  return data
}

/**
 * **Deneme sayısını sıfırlar** ve kaydı dış geçidin (Notifigate) kuyruğuna geri koyar (078-b):
 * sunucuda `RETRY_COUNT = 0`, `SEND_STATUS_ID = 20 (Gönderilecek)`, gönderim zamanı ve hata mesajı
 * temizlenir. Yetki: `SENT_EMAIL_MANAGE` (listeyi görme yetkisinden ayrı).
 *
 * `PUT` (gövdesiz): bu kod tabanında idempotent, gövdesiz ve güncellenmiş kaydı dönen durum
 * geçişlerinin şekli budur (en yakın emsal: aynı tablodaki `PUT /notifications/{id}/read`).
 * `modDate` gönderilmez — gerekçe `SentEmailAdminRepository#resetRetry` javadoc'unda ve
 * `specs/078-gonderilen-eposta/plan.md` → Complexity Tracking'de.
 *
 * Dönen değer **güncellenmiş liste satırıdır** → çağıran onu önbellekteki satırın yerine koyar.
 * Silinmiş/olmayan kimlikte **404**; **web kanalı** satırında **409 `INVALID_STATE`** (arayüz o
 * satırlarda butonu zaten göstermez — `retryResettable`).
 */
export async function resetSentEmailRetry(id: number): Promise<SentEmailListItem> {
  const { data } = await apiClient.put<SentEmailListItem>(`${BASE}/${id}/retry-reset`)
  return data
}

/**
 * Listeyi Excel (.xlsx) olarak indirir — liste ucuyla **aynı** filtreler; sunucu tüm filtreli
 * kayıtları döndürür (sayfalama yok). İndirme mekaniği ve dosya adı ortak `downloadXlsx`'tedir.
 */
export async function exportSentEmails(filters: SentEmailFilters): Promise<void> {
  // Dosya adı 058'in kişisel `bildirimler.xlsx`'inden bilinçli olarak ayrıdır (bkz. controller).
  await downloadXlsx(`${BASE}/export`, toParams(filters), 'bildirim-gonderimleri.xlsx')
}
